import { rewardSymbol } from "#lib/signup10/reward.js";

// Seasonal campaigns. One module decides which offer is on, and the banner,
// the survey copy and the minted discount (lib/server/signup-discount.js) all
// ask it, so the page can never promise a reward the code does not give.
//
// A campaign switches itself on and off by date: nobody has to deploy at
// midnight. Prerendered pages carry whatever was true at build time;
// +layout.svelte asks again in the browser on mount, which is what turns the
// banner off on a page built before the end date.

export const HALLOWEEN = Object.freeze({
  id: "halloween",
  startsAt: "2026-10-07T00:00:00Z",
  // Midnight after 31 October in Los Angeles (PDT, UTC-7), so the offer lasts
  // the whole of Halloween in every market we ship to.
  endsAt: "2026-11-01T07:00:00Z",
  // A percentage, where the everyday survey reward is a fixed €10 / $10.
  percent: 15,
  freeShipping: true,
  // {sym} becomes $ or € for the visitor (rewardSymbol). The banner's right
  // side (and on a phone its left), after "Click" or "Tap"; the button's
  // accessible name says "Get" instead.
  bannerTagline: "for {sym}30+ off",
  surveyTagline: "Save {sym}30+",
  // Pack sizes the survey code does not apply to. The 4-pack's own discount
  // combines with free shipping only (Shopify, 2026-10-07), so the card and
  // the code reveal say so. Must match that discount's combinations.
  excludedQty: [4],
  // The product page's gallery video while the campaign runs: the same square
  // cut as the everyday one, 720px for phones and 1000px for desktop, H.264
  // and VP9, no audio (it plays muted). Its own lettering runs edge to edge,
  // which the square stage shows whole.
  galleryVideo: {
    mobile: "/videos/campaigns/halloween/gallery-mobile.mp4",
    desktop: "/videos/campaigns/halloween/gallery-desktop.mp4",
    poster: "/images/campaigns/halloween/gallery-poster.webp",
  },
});

const CAMPAIGNS = [HALLOWEEN];

/** The campaign running at `now`, or null. */
export function activeCampaign(now = new Date()) {
  const t = now.getTime();
  return (
    CAMPAIGNS.find(
      (c) => t >= Date.parse(c.startsAt) && t < Date.parse(c.endsAt),
    ) || null
  );
}

/**
 * Whether the campaign's code skips a cart of `qty` units (`excludedQty`).
 * The checkout hand-off keeps the code back from such a cart and the product
 * page says so, both from this one answer.
 */
export function excludesQty(campaign, qty) {
  return campaign?.excludedQty?.includes(qty) === true;
}

/** Look a campaign up by the id the server handed the page. */
export function campaignById(id) {
  return CAMPAIGNS.find((c) => c.id === id) || null;
}

/** What the survey gives, as the page says it: "15%" or "€10" / "$10". */
export function rewardLabel(campaign, country) {
  return campaign ? `${campaign.percent}%` : `${rewardSymbol(country)}10`;
}

/** A campaign line with the visitor's currency symbol filled in. */
export function withSymbol(text, country) {
  return text.replaceAll("{sym}", rewardSymbol(country));
}

/**
 * What a setup costs once the survey code is on it, for the product page's
 * offer button. Rounded up to a whole amount: Shopify spreads the percentage
 * over the lines and lands a few cents off the plain product (304.32 for a
 * pair at 358), and the page never shows less than checkout charges.
 */
export function campaignPrice(campaign, total) {
  if (!campaign?.percent) return null;
  const cents = Math.round(Number(total) * (100 - campaign.percent));
  return Math.ceil(cents / 100);
}
